import { Mail, Phone, MapPin, Globe, Linkedin } from "lucide-react";

function ContactRow({ pi, color = "#475569" }) {
  const items = [
    pi.email && { icon: <Mail className="w-3 h-3" />, t: pi.email },
    pi.phone && { icon: <Phone className="w-3 h-3" />, t: pi.phone },
    pi.location && { icon: <MapPin className="w-3 h-3" />, t: pi.location },
    pi.website && { icon: <Globe className="w-3 h-3" />, t: pi.website },
    pi.linkedin && { icon: <Linkedin className="w-3 h-3" />, t: pi.linkedin },
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]" style={{ color }}>
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-1">{it.icon}{it.t}</span>
      ))}
    </div>
  );
}

function Bullets({ items }) {
  return (
    <ul className="mt-1.5 space-y-1 text-[12px] leading-snug list-disc pl-5">
      {(items || []).filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}
    </ul>
  );
}

// Shared, centered Key Skills block — used at the top of every template,
// directly under the Summary. Inherits the template's accent color.
function KeySkills({ skills, accent = "#0f172a", labelColor }) {
  if (!skills?.length) return null;
  return (
    <section className="mt-4 text-center" data-testid="resume-key-skills">
      <div
        className="text-[11px] font-bold uppercase tracking-[0.18em]"
        style={{ color: labelColor || accent }}
      >
        Key Skills
      </div>
      <p className="mt-1 text-[12px] leading-relaxed">
        {skills.filter(Boolean).join("  •  ")}
      </p>
    </section>
  );
}

// ----- CLASSIC / TRADITIONAL ------------------------------------------------
export function ClassicTemplate({ resume }) {
  const pi = resume.personalInfo || {};
  const accent = resume.accentColor || "#0f172a";
  return (
    <div className="resume-page" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <div className="text-center pb-3" style={{ borderBottom: `2px solid ${accent}` }}>
        <div className="text-[28px] font-bold tracking-wide uppercase" style={{ color: accent }}>{pi.fullName || "Your Name"}</div>
        <div className="mt-1"><ContactRow pi={pi} /></div>
      </div>
      {resume.summary && (
        <Section title="Summary" accent={accent}>
          <p className="text-[12px] leading-snug">{resume.summary}</p>
        </Section>
      )}
      <KeySkills skills={resume.skills} accent={accent} />
      {resume.experience?.length > 0 && (
        <Section title="Experience" accent={accent}>
          {resume.experience.map((e, i) => (
            <div key={i} className="mb-3">
              <div className="flex justify-between text-[12px]">
                <span className="font-bold">{e.role}</span>
                <span className="italic">{e.startDate}{e.endDate || e.current ? ` — ${e.current ? "Present" : e.endDate}` : ""}</span>
              </div>
              <div className="text-[12px] italic">{e.company}{e.location ? `, ${e.location}` : ""}</div>
              <Bullets items={e.bullets} />
            </div>
          ))}
        </Section>
      )}
      {resume.education?.length > 0 && (
        <Section title="Education" accent={accent}>
          {resume.education.map((ed, i) => (
            <div key={i} className="mb-2 text-[12px]">
              <div className="flex justify-between"><span className="font-bold">{ed.degree}</span><span className="italic">{ed.startDate}{ed.endDate ? ` — ${ed.endDate}` : ""}</span></div>
              <div className="italic">{ed.school}</div>
              {ed.details && <div className="text-[11px] mt-0.5">{ed.details}</div>}
            </div>
          ))}
        </Section>
      )}
      {resume.projects?.length > 0 && (
        <Section title="Projects" accent={accent}>
          {resume.projects.map((p, i) => (
            <div key={i} className="mb-2 text-[12px]">
              <div className="font-bold">{p.name}</div>
              {p.description && <div className="text-[11px]">{p.description}</div>}
            </div>
          ))}
        </Section>
      )}
      {resume.showReferences && resume.references?.length > 0 && (
        <Section title="References" accent={accent}>
          <div className="grid grid-cols-2 gap-3">
            {resume.references.map((r, i) => (
              <div key={i} className="text-[11.5px]">
                <div className="font-bold">{r.name}{r.type === "personal" ? " (Personal)" : ""}</div>
                {r.title && <div className="italic">{r.title}</div>}
                {(r.email || r.phone) && (
                  <div>{[r.email, r.phone].filter(Boolean).join(" • ")}</div>
                )}
                {r.note && <div className="text-[10.5px] mt-0.5 italic">{r.note}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}
      {!resume.showReferences && (
        <div className="mt-6 text-center text-[11.5px] italic" style={{ color: accent }}>
          References available upon request.
        </div>
      )}
    </div>
  );
}
function Section({ title, children, accent = "#0f172a" }) {
  return (
    <div className="mt-4">
      <div
        className="text-[13px] font-bold uppercase tracking-wider pb-1"
        style={{ color: accent, borderBottom: `1px solid ${accent}33` }}
      >
        {title}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

// ----- MODERN (2-column) -----------------------------------------------------
export function ModernTemplate({ resume }) {
  const pi = resume.personalInfo || {};
  const accent = resume.accentColor || "#2563EB";
  return (
    <div className="resume-page" style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif" }}>
      <div className="grid grid-cols-3 gap-5">
        <aside className="col-span-1 bg-slate-50 -ml-[0.7in] -my-[0.6in] py-[0.6in] px-5 border-r border-slate-200">
          <div className="text-[20px] font-bold leading-tight" style={{ color: accent }}>{pi.fullName || "Your Name"}</div>
          <div className="mt-3 space-y-1 text-[11px] text-slate-600">
            {pi.email && <div className="break-all">{pi.email}</div>}
            {pi.phone && <div>{pi.phone}</div>}
            {pi.location && <div>{pi.location}</div>}
            {pi.website && <div className="break-all">{pi.website}</div>}
            {pi.linkedin && <div className="break-all">{pi.linkedin}</div>}
          </div>
          {resume.education?.length > 0 && (
            <div className="mt-5">
              <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>Education</div>
              {resume.education.map((ed, i) => (
                <div key={i} className="mt-2 text-[12px]">
                  <div className="font-semibold">{ed.degree}</div>
                  <div>{ed.school}</div>
                  <div className="text-[11px] text-slate-500">{ed.startDate}{ed.endDate ? ` — ${ed.endDate}` : ""}</div>
                </div>
              ))}
            </div>
          )}
        </aside>
        <main className="col-span-2">
          {resume.summary && (
            <div>
              <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>Summary</div>
              <p className="mt-1.5 text-[12px] leading-snug">{resume.summary}</p>
            </div>
          )}
          <KeySkills skills={resume.skills} accent={accent} />
          {resume.experience?.length > 0 && (
            <div className="mt-5">
              <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>Experience</div>
              {resume.experience.map((e, i) => (
                <div key={i} className="mt-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="font-semibold">{e.role}</span>
                    <span className="text-slate-500 text-[11px]">{e.startDate}{e.endDate || e.current ? ` — ${e.current ? "Present" : e.endDate}` : ""}</span>
                  </div>
                  <div className="text-[12px] text-slate-600">{e.company}{e.location ? ` • ${e.location}` : ""}</div>
                  <Bullets items={e.bullets} />
                </div>
              ))}
            </div>
          )}
          {resume.projects?.length > 0 && (
            <div className="mt-5">
              <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>Projects</div>
              {resume.projects.map((p, i) => (
                <div key={i} className="mt-2 text-[12px]">
                  <div className="font-semibold">{p.name}</div>
                  {p.description && <div>{p.description}</div>}
                </div>
              ))}
            </div>
          )}
          {resume.showReferences && resume.references?.length > 0 && (
            <div className="mt-5">
              <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>References</div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {resume.references.map((r, i) => (
                  <div key={i} className="text-[11.5px]">
                    <div className="font-semibold">{r.name}{r.type === "personal" ? " (Personal)" : ""}</div>
                    {r.title && <div className="text-slate-600">{r.title}</div>}
                    {(r.email || r.phone) && (
                      <div className="text-slate-600">{[r.email, r.phone].filter(Boolean).join(" • ")}</div>
                    )}
                    {r.note && <div className="text-[10.5px] mt-0.5 italic text-slate-500">{r.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!resume.showReferences && (
            <div className="mt-6 text-[11.5px] italic" style={{ color: accent }}>
              References available upon request.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ----- MINIMAL ---------------------------------------------------------------
export function MinimalTemplate({ resume }) {
  const pi = resume.personalInfo || {};
  return (
    <div className="resume-page" style={{ fontFamily: "'Outfit', 'IBM Plex Sans', sans-serif" }}>
      <div>
        <div className="text-[30px] font-light tracking-tight">{pi.fullName || "Your Name"}</div>
        <ContactRow pi={pi} color="#94A3B8" />
      </div>
      {resume.summary && (
        <div className="mt-6">
          <p className="text-[12.5px] leading-relaxed text-slate-700">{resume.summary}</p>
        </div>
      )}
      <KeySkills skills={resume.skills} accent="#0f172a" labelColor="#94A3B8" />
      {resume.experience?.length > 0 && (
        <Block title="Experience">
          {resume.experience.map((e, i) => (
            <div key={i} className="mb-4">
              <div className="text-[13px] font-medium">{e.role} <span className="text-slate-400 font-normal">— {e.company}</span></div>
              <div className="text-[10.5px] text-slate-400 uppercase tracking-wider">{e.startDate}{e.endDate || e.current ? ` — ${e.current ? "Present" : e.endDate}` : ""}{e.location ? ` · ${e.location}` : ""}</div>
              <Bullets items={e.bullets} />
            </div>
          ))}
        </Block>
      )}
      {resume.education?.length > 0 && (
        <Block title="Education">
          {resume.education.map((ed, i) => (
            <div key={i} className="mb-2">
              <div className="text-[13px] font-medium">{ed.degree} <span className="text-slate-400">— {ed.school}</span></div>
              <div className="text-[10.5px] text-slate-400 uppercase tracking-wider">{ed.startDate}{ed.endDate ? ` — ${ed.endDate}` : ""}</div>
            </div>
          ))}
        </Block>
      )}
      {resume.projects?.length > 0 && (
        <Block title="Projects">
          {resume.projects.map((p, i) => (
            <div key={i} className="mb-2 text-[12px]"><span className="font-medium">{p.name}</span> — <span className="text-slate-600">{p.description}</span></div>
          ))}
        </Block>
      )}
    </div>
  );
}
function Block({ title, children }) {
  return (
    <div className="mt-6">
      <div className="text-[10.5px] uppercase tracking-[0.2em] text-slate-400">{title}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

// ----- CREATIVE --------------------------------------------------------------
export function CreativeTemplate({ resume }) {
  const pi = resume.personalInfo || {};
  return (
    <div className="resume-page" style={{ fontFamily: "'Outfit', sans-serif", padding: 0 }}>
      <div className="px-[0.7in] py-[0.5in]" style={{ background: "#0f172a", color: "white" }}>
        <div className="text-[34px] font-bold leading-none tracking-tight">{pi.fullName || "Your Name"}</div>
        <div className="mt-2 text-[12px] text-slate-300">
          <ContactRow pi={pi} color="#cbd5e1" />
        </div>
      </div>
      <div className="px-[0.7in] py-[0.5in]">
        {resume.summary && (
          <div>
            <div className="text-[13px] font-bold text-amber-600 uppercase tracking-widest">About</div>
            <p className="mt-1.5 text-[12.5px] leading-relaxed">{resume.summary}</p>
          </div>
        )}
        {resume.skills?.length > 0 && (
          <KeySkills skills={resume.skills} accent="#d97706" />
        )}
        {resume.experience?.length > 0 && (
          <div className="mt-5">
            <div className="text-[13px] font-bold text-amber-600 uppercase tracking-widest">Experience</div>
            {resume.experience.map((e, i) => (
              <div key={i} className="mt-3 grid grid-cols-4 gap-3">
                <div className="col-span-1 text-[11px] text-slate-500">
                  {e.startDate}<br />{e.current ? "Present" : e.endDate}
                </div>
                <div className="col-span-3">
                  <div className="text-[13px] font-semibold">{e.role}</div>
                  <div className="text-[12px] text-slate-600">{e.company}{e.location ? ` · ${e.location}` : ""}</div>
                  <Bullets items={e.bullets} />
                </div>
              </div>
            ))}
          </div>
        )}
        {resume.education?.length > 0 && (
          <div className="mt-5">
            <div className="text-[13px] font-bold text-amber-600 uppercase tracking-widest">Education</div>
            {resume.education.map((ed, i) => (
              <div key={i} className="mt-2 grid grid-cols-4 gap-3 text-[12px]">
                <div className="col-span-1 text-[11px] text-slate-500">{ed.startDate}{ed.endDate ? ` — ${ed.endDate}` : ""}</div>
                <div className="col-span-3">
                  <div className="font-semibold">{ed.degree}</div>
                  <div className="text-slate-600">{ed.school}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ----- PROFESSIONAL ---------------------------------------------------------
export function ProfessionalTemplate({ resume }) {
  const pi = resume.personalInfo || {};
  const slate = "#1e3a8a";
  return (
    <div className="resume-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="border-b-4 pb-3" style={{ borderColor: slate }}>
        <div className="text-[26px] font-bold" style={{ color: slate }}>{pi.fullName || "Your Name"}</div>
        <div className="mt-1"><ContactRow pi={pi} /></div>
      </div>
      {resume.summary && (
        <div className="mt-4">
          <div className="text-[12px] font-bold uppercase tracking-widest" style={{ color: slate }}>Professional Summary</div>
          <p className="mt-1 text-[12px] leading-snug">{resume.summary}</p>
        </div>
      )}
      <KeySkills skills={resume.skills} accent={slate} />
      {resume.experience?.length > 0 && (
        <div className="mt-4">
          <div className="text-[12px] font-bold uppercase tracking-widest" style={{ color: slate }}>Experience</div>
          {resume.experience.map((e, i) => (
            <div key={i} className="mt-2.5">
              <div className="flex justify-between text-[12.5px]">
                <span className="font-bold">{e.role} — {e.company}</span>
                <span>{e.startDate}{e.endDate || e.current ? ` to ${e.current ? "Present" : e.endDate}` : ""}</span>
              </div>
              <div className="text-[11px] text-slate-500">{e.location}</div>
              <Bullets items={e.bullets} />
            </div>
          ))}
        </div>
      )}
      <div className="mt-4">
        {resume.education?.length > 0 && (
          <div>
            <div className="text-[12px] font-bold uppercase tracking-widest" style={{ color: slate }}>Education</div>
            {resume.education.map((ed, i) => (
              <div key={i} className="mt-2 text-[12px]">
                <div className="font-bold">{ed.degree}</div>
                <div>{ed.school}</div>
                <div className="text-[11px] text-slate-500">{ed.startDate}{ed.endDate ? ` — ${ed.endDate}` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {resume.projects?.length > 0 && (
        <div className="mt-4">
          <div className="text-[12px] font-bold uppercase tracking-widest" style={{ color: slate }}>Selected Projects</div>
          {resume.projects.map((p, i) => (
            <div key={i} className="mt-2 text-[12px]"><span className="font-bold">{p.name}.</span> {p.description}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ----- CENTERED (Combination / Functional - matches user's docx template) ---
export function CenteredTemplate({ resume }) {
  const pi = resume.personalInfo || {};
  const accent = resume.accentColor || "#0f172a";
  const contactLine = [pi.location, pi.phone, pi.email].filter(Boolean).join("  •  ");
  const links = [pi.website, pi.linkedin].filter(Boolean).join("  •  ");
  const allBullets = (resume.experience || [])
    .flatMap((e) => e.bullets || [])
    .filter((b) => b && b.trim());
  const headerStyle = { color: accent, borderBottom: `1px solid ${accent}33` };
  return (
    <div className="resume-page" style={{ fontFamily: "'IBM Plex Sans', Arial, Helvetica, sans-serif", color: "#0f172a" }}>
      <header className="text-center">
        <div className="text-[26px] font-bold leading-tight uppercase tracking-wide">{pi.fullName || "First and Last Name"}</div>
        {contactLine && <div className="mt-1 text-[12px] text-slate-700">{contactLine}</div>}
        {links && <div className="mt-0.5 text-[11px] text-slate-500">{links}</div>}
        {pi.headline && (
          <div className="mt-2 text-[14px] font-semibold tracking-wide" style={{ color: accent }}>{pi.headline}</div>
        )}
      </header>

      {resume.summary && (
        <section className="mt-4">
          <p className="text-[12px] leading-relaxed text-slate-800">{resume.summary}</p>
        </section>
      )}

      <KeySkills skills={resume.skills} accent={accent} />

      {allBullets.length > 0 && (
        <section className="mt-5">
          <h2 className="text-[13px] font-bold uppercase tracking-widest pb-1" style={headerStyle}>
            Professional Experience
          </h2>
          <ul className="mt-2 list-disc pl-5 space-y-1 text-[12px] leading-snug">
            {allBullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        </section>
      )}

      {resume.experience?.length > 0 && (
        <section className="mt-5">
          <h2 className="text-[13px] font-bold uppercase tracking-widest pb-1" style={headerStyle}>
            Work History
          </h2>
          <div className="mt-2 space-y-1">
            {resume.experience.map((e, i) => {
              const right = `${e.startDate || ""}${e.endDate || e.current ? ` — ${e.current ? "Present" : e.endDate}` : ""}`;
              return (
                <div key={i} className="flex justify-between gap-3 text-[12px]">
                  <span><span className="font-semibold">{e.role || ""}</span>{e.company || e.location ? `, ${[e.company, e.location].filter(Boolean).join(", ")}` : ""}</span>
                  <span className="text-slate-600 whitespace-nowrap">{right}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {resume.education?.length > 0 && (
        <section className="mt-5">
          <h2 className="text-[13px] font-bold uppercase tracking-widest pb-1" style={headerStyle}>
            Education and Training
          </h2>
          <div className="mt-2 space-y-1 text-[12px]">
            {resume.education.map((ed, i) => {
              const parts = [ed.degree, ed.school, ed.details].filter(Boolean).join(", ");
              const dates = `${ed.startDate || ""}${ed.endDate ? ` — ${ed.endDate}` : ""}`;
              return (
                <div key={i} className="flex justify-between gap-3">
                  <span>{parts}</span>
                  {dates.trim() && <span className="text-slate-600 whitespace-nowrap">{dates}</span>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {resume.projects?.length > 0 && (
        <section className="mt-5">
          <h2 className="text-[13px] font-bold uppercase tracking-widest pb-1" style={headerStyle}>
            Projects
          </h2>
          <div className="mt-2 space-y-1 text-[12px]">
            {resume.projects.map((p, i) => (
              <div key={i}>
                <span className="font-semibold">{p.name}</span>
                {p.description ? ` — ${p.description}` : ""}
              </div>
            ))}
          </div>
        </section>
      )}

      {resume.showReferences && resume.references?.length > 0 && (
        <section className="mt-5">
          <h2 className="text-[13px] font-bold uppercase tracking-widest pb-1" style={headerStyle}>
            References
          </h2>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-[11.5px]">
            {resume.references.map((r, i) => (
              <div key={i}>
                <div className="font-semibold">{r.name}{r.type === "personal" ? " (Personal)" : ""}</div>
                {r.title && <div>{r.title}</div>}
                {(r.email || r.phone) && (
                  <div className="text-slate-600">{[r.email, r.phone].filter(Boolean).join(" • ")}</div>
                )}
                {r.note && <div className="text-[10.5px] mt-0.5 italic text-slate-500">{r.note}</div>}
              </div>
            ))}
          </div>
        </section>
      )}
      {!resume.showReferences && (
        <div className="mt-8 text-center text-[11.5px] italic" style={{ color: accent }}>
          References available upon request.
        </div>
      )}
    </div>
  );
}

export const TEMPLATE_COMPONENTS = {
  centered: CenteredTemplate,
  classic: ClassicTemplate,
  modern: ModernTemplate,
  minimal: MinimalTemplate,
  creative: CreativeTemplate,
  professional: ProfessionalTemplate,
};
